import { useEffect, useState } from 'react'
import { Panel } from '@/components/erp/primitives'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { Field } from '@/shared/ui/Modal'
import { explain } from '@/shared/api/problem'
import { UnitCascade, type CascadeValue } from '@/features/voters/UnitCascade'
import type { SurveyGroupDto, SurveyResponseDto, SurveyWaveDto } from '@/shared/api/types'
import { createPoll, getGroup, updatePoll } from './api'
import { cn } from '@/lib/utils'
import { useCodes } from './useVocabulary'
import { ChoiceCards, ChoiceSelect, StepField } from './ConfigChoice'
import {
  audienceImplication, audienceLabel, audioModeImplication, audioModeLabel,
  categoryImplication, categoryLabel, methodologyImplication, methodologyLabel,
  purposeImplication, purposeLabel,
} from './vocab'

/**
 * Step 1 — what this poll is for, and where it operates.
 *
 * ## This step is where the poll comes into existence
 *
 * On a new poll, "Save and continue" is the create call; on an existing one it
 * is the update. The wizard cannot defer creation to the end, because every
 * step after this one is keyed on a poll id: the sample-size verdict reads the
 * poll's declared frame, the link picker is narrowed by the poll's scope, and
 * the schedule hangs off the poll's tracker. A wizard that held six steps of
 * state in the browser and posted them at the end would also lose all six to a
 * refresh.
 *
 * ## Scope is required, and it is required HERE
 *
 * A poll with no scope cannot be weighted, cannot be assigned to anybody (the
 * assignment guard has nothing to test against) and cannot be filtered by
 * anybody's geography. The server refuses one; asking for it on the first step
 * is what stops somebody filling in six steps and then discovering it.
 */
export function ConfigScopeStep({
  poll, groups, onSaved,
}: {
  poll: SurveyResponseDto | null
  groups: SurveyGroupDto[]
  onSaved: (poll: SurveyResponseDto) => void
}) {
  const categories = useCodes('category')
  const purposes = useCodes('purpose')
  const audiences = useCodes('audience')
  const methods = useCodes('methodology')
  const audioModes = useCodes('audioRecordingMode').codes

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [purpose, setPurpose] = useState('')
  const [audience, setAudience] = useState('')
  const [method, setMethod] = useState('')
  const [groupId, setGroupId] = useState('')
  const [waveId, setWaveId] = useState('')
  const [unit, setUnit] = useState<CascadeValue>({ path: [] })
  /** Editing an existing scope is opt-in — see `scopeIsChosen` below. */
  const [changingScope, setChangingScope] = useState(false)
  const [audioMode, setAudioMode] = useState('')
  const [waves, setWaves] = useState<SurveyWaveDto[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Seeded from the poll ONCE per poll, by id. A dependency on the whole object
  // would re-seed on every save and throw away edits made after it.
  useEffect(() => {
    if (!poll) return
    setTitle(poll.title)
    setDescription(poll.description ?? '')
    setCategory(poll.categoryCode)
    setPurpose(poll.purposeCode)
    setAudience(poll.audienceCode)
    setMethod(poll.methodologyCode)
    setGroupId(poll.groupId ?? '')
    setAudioMode(poll.audioRecordingMode ?? 'OFF')
    setWaveId(poll.waveId ?? '')
    setUnit({ path: [] })
    setChangingScope(false)
  }, [poll?.id])

  /**
   * The tracker's waves, which the LIST does not carry.
   *
   * `SurveyGroupDto.waves` is documented as empty on list rows and populated on
   * the detail read. Rendering the list row's empty array as the wave picker's
   * options would show "no waves" for a tracker that has four.
   */
  useEffect(() => {
    if (!groupId) { setWaves([]); return }
    let alive = true
    getGroup(groupId)
      .then((g) => { if (alive) setWaves(g.waves ?? []) })
      .catch(() => { if (alive) setWaves([]) })
    return () => { alive = false }
  }, [groupId])

  // The first SERVED code is the default, never a string written here. A
  // hardcoded 'BASELINE' is the drift this feature's vocabulary exists to stop,
  // one value smaller.
  const categoryValue = category || categories.codes[0] || ''
  const purposeValue = purpose || purposes.codes[0] || ''
  const audienceValue = audience || audiences.codes[0] || ''
  const methodValue = method || methods.codes[0] || ''

  const selectedGroup = groups.find((g) => g.id === groupId)
  /**
   * The saved scope stands until somebody replaces it.
   *
   * `UnitCascade` repopulates its selects from a `path` of ancestor ids, and the
   * poll carries ancestor NAMES only (`scopeUnitLabels`). Seeding it with a bare
   * `unitId` would render four empty dropdowns above a poll that does have a
   * scope — which reads as "unset" and invites somebody to re-pick it. So an
   * existing scope is shown as text, and changing it is a deliberate click.
   */
  const scopeIsChosen = !!poll?.scopeUnitId && !changingScope
  const scopeUnitId = scopeIsChosen ? poll!.scopeUnitId : unit.unitId ?? null
  const ready = title.trim().length > 2 && !!scopeUnitId && !!categoryValue && !!methodValue

  async function save() {
    setBusy(true)
    setError(null)
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || null,
        categoryCode: categoryValue,
        purposeCode: purposeValue,
        audienceCode: audienceValue,
        methodologyCode: methodValue,
        scopeUnitId,
        groupId: groupId || null,
        waveId: waveId || null,
        // Carried through unchanged on an edit; the next step owns them. Sending
        // them is not optional — the server's update replaces the whole poll,
        // so omitting a field it already has would clear it.
        targetSample: poll?.targetSample ?? 400,
        confidenceLevel: poll?.confidenceLevel ?? 95,
        targetMoe: poll?.targetMoe ?? null,
        audioRecordingMode: audioMode || poll?.audioRecordingMode || 'OFF',
        consentRequired: poll?.consentRequired ?? null,
        randomiseQuestionOrder: poll?.randomiseQuestionOrder ?? null,
        randomiseOptionOrder: poll?.randomiseOptionOrder ?? null,
        allowOffline: poll?.allowOffline ?? null,
        opensAt: poll?.opensAt ?? null,
        closesAt: poll?.closesAt ?? null,
        linkedSeatType: poll?.linkedSeatType ?? null,
        retentionDays: poll?.retentionDays ?? null,
      }
      onSaved(poll ? await updatePoll(poll.id, body) : await createPoll(body))
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel
      title="Purpose and scope"
      description="What this poll is for, and where it operates"
      right={(
        <button type="button" className="erp-btn-primary h-8 text-erp-xs"
          disabled={!ready || busy} onClick={save}>
          {busy ? 'Saving…' : poll ? 'Save' : 'Create poll'}
        </button>
      )}
    >
      <div className="space-y-4">
        <ErrorStrip message={error} />

        <Field label="Title" required>
          <input className="erp-input h-9 w-full" value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="August governor tracker, wave 3" />
        </Field>

        <Field label="Description"
          hint="What this poll measures, who is asking, and how the results will be used.">
          <textarea className="erp-input w-full min-h-[60px] py-2" value={description}
            onChange={(e) => setDescription(e.target.value)} />
        </Field>

        <StepField label="Category" required
          hint="How this poll is filed. It also decides what the rest of the studio expects of it.">
          <ChoiceCards state={categories} value={categoryValue} onChange={setCategory}
            labelOf={categoryLabel} implicationOf={categoryImplication} />
        </StepField>

        <StepField label="Purpose"
          hint="Why it is being fielded. This is the field a reader checks a year later to know what the numbers were for.">
          <ChoiceCards state={purposes} value={purposeValue} onChange={setPurpose}
            labelOf={purposeLabel} implicationOf={purposeImplication} />
        </StepField>

        <div className="grid gap-3 sm:grid-cols-2">
          <StepField label="Audience">
            <ChoiceSelect state={audiences} value={audienceValue} onChange={setAudience}
              labelOf={audienceLabel} implicationOf={audienceImplication} />
          </StepField>
          <StepField label="Method">
            <ChoiceSelect state={methods} value={methodValue} onChange={setMethod}
              labelOf={methodologyLabel} implicationOf={methodologyImplication} />
          </StepField>
        </div>

        <StepField label="Geographic scope" required
          hint="Everything downstream reads this: who may be assigned, whose results are visible, and what the map aggregates to.">
          {scopeIsChosen ? (
            <div className="flex items-center justify-between gap-3 border border-border rounded-md px-3 py-2">
              <span className="text-erp-sm min-w-0 truncate">
                {poll!.scopeUnitLabels?.length
                  ? poll!.scopeUnitLabels.join(' › ')
                  : poll!.scopeUnitName ?? poll!.scopeUnitId}
              </span>
              <button type="button" className="erp-btn-ghost h-7 text-erp-xs shrink-0"
                disabled={!!poll?.launchedAt}
                onClick={() => setChangingScope(true)}>
                Change
              </button>
            </div>
          ) : (
            <UnitCascade value={unit} onChange={setUnit} required />
          )}
          {poll?.launchedAt && (
            <Note tone="bad">
              This poll has been in the field since {new Date(poll.launchedAt).toLocaleDateString()},
              so its scope can no longer change. Interviews already collected were sampled under
              the old scope, and moving it would put two different samples in one dataset.
            </Note>
          )}
        </StepField>

        <div className="grid gap-3 sm:grid-cols-2">
          <StepField label="Tracker"
            hint="A poll in a tracker can be compared with its other waves — and can be put on a schedule.">
            <select className="erp-input h-9 w-full" value={groupId}
              onChange={(e) => { setGroupId(e.target.value); setWaveId('') }}>
              <option value="">Standalone — not part of a tracker</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.code} · {g.name}</option>
              ))}
            </select>
          </StepField>
          <StepField label="Wave"
            hint={selectedGroup
              ? 'Which run of the tracker this is. The trend screen lines waves up by number.'
              : 'Pick a tracker first — a wave is a run OF something.'}>
            <select className="erp-input h-9 w-full" value={waveId} disabled={!selectedGroup}
              onChange={(e) => setWaveId(e.target.value)}>
              <option value="">No wave</option>
              {waves.map((w) => (
                <option key={w.id} value={w.id}>
                  Wave {w.waveNo}{w.label ? ` · ${w.label}` : ''}
                </option>
              ))}
            </select>
          </StepField>
        </div>

        <StepField label="Interview audio"
          hint="Whether the interview itself is recorded. Recording begins when capture opens, so the consent script has to say so — the server refuses to launch a recording poll whose script does not.">
          <div className="flex flex-wrap gap-1.5">
            {audioModes.map((code) => (
              <button key={code} type="button"
                onClick={() => setAudioMode(code)}
                className={cn(
                  'h-8 px-3 text-erp-xs rounded border transition-colors',
                  code === (audioMode || 'OFF')
                    ? 'bg-chocolate text-white border-chocolate'
                    : 'bg-background border-border hover:bg-muted',
                )}>
                {audioModeLabel(code)}
              </button>
            ))}
          </div>
          <p className="text-erp-xs text-muted-foreground">
            {audioModeImplication(audioMode || 'OFF')}
          </p>
        </StepField>

        {!poll && (
          <Note>
            Creating the poll here rather than at the end is deliberate: every step after this
            one is keyed on it. The poll starts as a draft with an empty instrument and collects
            nothing until it is launched.
          </Note>
        )}
      </div>
    </Panel>
  )
}
